import { ChangeEvent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@twind/core';
import useInputFontSizeClass from 'app/hooks/useInputFontSizeClass';
import useWebchatVersion from 'app/hooks/useWebchatVersion';
import { ChatVersion } from 'app/copied/constants/common';
import IntegratedSearchInput from 'app/modules/tailor/components/IntegratedSearchInput';
import useCustomization from 'app/hooks/useCustomization';

import LiveChatSendIcon from '../../../components/icons/LiveChatSendIcon';

type Props = {
  message: string;
  onSend: (message: string) => void;
  onChange: (value: string) => void;
  disabled?: boolean;
};

const LiveChatPrompt = ({ message, disabled, onChange, onSend }: Props) => {
  const customization = useCustomization();
  const webchatVersion = useWebchatVersion();
  const { t } = useTranslation();
  const fontSizeClass = useInputFontSizeClass();
  const handleKeyUp = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      if (message.trim().length > 0) {
        event.preventDefault();
        onSend(message);
      }
    }
  };

  const handleChange = ({ target }: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(target.value);
  };

  const handleClick = () => {
    if (message.trim().length > 0) {
      onSend(message);
    }
  };

  if (webchatVersion === ChatVersion.v4_1 || customization?.fe_config?.v40UnityOverrides?.useUnitySearchBar) {
    return (
      <div className="p-3 shrink-0">
        <IntegratedSearchInput
          value={message}
          onChange={onChange}
          onClickSearch={onSend}
          disableWhenEmpty={true}
          placeholder={t('virtual_assistant.message_placeholder')}
          ariaLabel={t('aria_labels.live_chat_search')}
        />
      </div>
    );
  }

  return (
    <div
      id="ostro-live-chat-prompt-container"
      className="py-2 px-3 shrink-0 bg-option"
    >
      <div className="flex items-center gap-1">
        <fieldset
          disabled={disabled}
          className="flex w-full gap-2 relative items-center disabled:opacity-50"
        >
          <textarea
            id="live-chat-prompt-textarea"
            value={message}
            rows={1}
            placeholder={t('live_chat_prompt_placeholder')}
            className={cx(
              'grow md:text-sm min-h-[50px] max-h-24 leading-6 resize-none pl-3 pr-[50px] py-3 rounded-2xl bg-white border-none focus:outline focus:outline-brand-primary outline-2',
              fontSizeClass
            )}
            onChange={handleChange}
            onKeyDown={handleKeyUp}
            aria-placeholder={t('live_chat_prompt_placeholder')}
          />
          <button
            id="live-chat-send-button"
            className="absolute bottom-[7px] right-[6px] w-[36px] h-[36px] shrink-0 p-0 rounded-full"
            onClick={handleClick}
            aria-label="Send Message"
          >
            <LiveChatSendIcon />
          </button>
        </fieldset>
      </div>
    </div>
  );
};

export default LiveChatPrompt;
